/**
 * The scorebook's whole runtime state, in one hook.
 *
 * The ordering rule lives here and is the reason this is not three hooks:
 * every recorded play goes to disk, THEN to the screen, THEN to the network.
 * A tap that has changed the display has already survived an app kill.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appState, broadcast, haptics, ids, network, wakeLock } from '@good-season/platform';

import { afterServer } from './afterServer.js';
import {
  appendFrom,
  cachedGame,
  logFor,
  queueEvents,
  stampOwner,
  stampStatus,
  unsyncedCount,
  type CachedGame,
  type EventBody,
  type FinalScore,
  type LoggedEvent,
} from './db.js';
import {
  attributionBodies,
  clockAt,
  liveEvents,
  nextPeriod,
  onCourt,
  playBody,
  retypeBodies,
  substitutionBodies,
  tally,
  undoDrafts,
  voidDraft,
  voidGroup,
  type ClockState,
  type EventDraft,
} from './game.js';
import { OfflineError, api, post } from '../api/client.js';
import { confirmSignedIn, mayOpen, signedInForBooks } from './owner.js';
import { announceWrite, WRITES } from './nudge.js';
import { requestPersistence, startSync, unreadable, type SyncStatus } from './sync.js';

export type { EventDraft } from './game.js';

/** How long a half-made choice stays armed before it clears (BUILD_PLAN 5.4). */
export const SELECTION_TIMEOUT_MS = 4_000;

export type RetypeResult =
  | { readonly kind: 'done'; readonly row: LoggedEvent }
  | { readonly kind: 'busy' }
  | { readonly kind: 'gone'; readonly current: string | null };

/**
 * A play or a swap tapped while the screen showed a period that the book on
 * disk has since left (another window ended it, or an End period still being
 * written when the tap came). Nothing was written: the tap belongs to `shown`,
 * and putting it in `current` at that period's opening reading would be a
 * play in a period the coach did not mean (ADR-0110). The screen says so and
 * the coach enters it again if it belongs in `current`.
 */
export class PeriodMovedError extends Error {
  override readonly name = 'PeriodMovedError';
  readonly shown: number;
  readonly current: number;
  constructor(shown: number, current: number) {
    super(`tapped in period ${String(shown)}, but the book is in period ${String(current)}`);
    this.shown = shown;
    this.current = current;
  }
}

/** `ended`, or `already`: the period shown had ended, nothing was written. */
export type EndPeriodResult =
  { readonly kind: 'ended' } | { readonly kind: 'already'; readonly current: number };

export type Persistence = 'unknown' | 'granted' | 'denied' | 'unsupported';

export interface Scorebook {
  readonly game: CachedGame | null;
  /**
   * This phone holds the book, and it belongs to somebody other than whoever
   * is signed in here (#43). The screen shows none of it.
   */
  readonly refused: boolean;
  readonly log: readonly LoggedEvent[];
  /** The plays that still count, oldest first. What the timeline shows. */
  readonly plays: readonly LoggedEvent[];
  readonly status: SyncStatus;
  readonly persistence: Persistence;
  readonly loading: boolean;
  /**
   * The store on this phone refused to open, or null. A designed state, not a
   * loading screen that never ends: without it a browser blocking site data
   * pinned the book on "One moment" forever. Found by the fresh-context pass.
   */
  readonly failure: unknown;
  /** Ask the store again. `db.ts` forgets a failed open, so this is a real retry. */
  readonly retryOpen: () => void;
  /** As far as THIS device knows. Another device closing it arrives as a 409. */
  readonly finished: boolean;
  readonly clock: ReturnType<typeof clockAt>;
  readonly scores: ReturnType<typeof tally>;
  readonly floor: readonly string[];
  readonly canUndo: boolean;
  /** One tap's rows, written as ONE transaction and sharing one reading. */
  readonly record: (...drafts: readonly EventDraft[]) => Promise<readonly LoggedEvent[]>;
  readonly undo: () => Promise<void>;
  readonly voidPlay: (target: LoggedEvent) => Promise<void>;
  readonly attribute: (target: LoggedEvent, playerId: string) => Promise<void>;
  /**
   * Change a recorded foul to another kind, as a correction (`retypeBodies`).
   * Says which of three things happened: the row now standing for the foul;
   * `busy`, another correction was still being written and nothing was
   * recorded (the caller must say so, a tap that does nothing silently is the
   * failure this book exists to prevent); or `gone`, the foul was no longer
   * live, so there is nothing to change.
   */
  readonly retype: (target: LoggedEvent, type: string) => Promise<RetypeResult>;
  readonly substitute: (outPlayerId: string, inPlayerId: string) => Promise<void>;
  /** End the period the screen shows (`shown`), and open the next (`EndPeriodResult`). */
  readonly endPeriod: (shown: number) => Promise<EndPeriodResult>;
  readonly retryNow: () => void;
  readonly reopen: () => Promise<void>;
  /**
   * Whether a coach has already approved this book, asked of the server.
   * False on any failure: the reopen confirm then says the shorter thing.
   */
  readonly publishedOnServer: () => Promise<boolean>;
  /**
   * Close the book. Resolves to the official score the server answered with,
   * which is the first one committed and not necessarily the one passed in,
   * or null when the answer did not carry one.
   */
  readonly finish: (ourScore: number, theirScore: number) => Promise<FinalScore | null>;
  /** This game's rows the server has not named yet, read from disk. */
  readonly unsyncedHere: () => Promise<number>;
  /**
   * Read the book from disk again, the same read the open makes, and show it.
   * Rejects when the store will not answer.
   */
  readonly reread: () => Promise<void>;
}

const NO_SETUP = {
  regulationPeriods: 2,
  regulationLengthMs: 600_000,
  overtimeLengthMs: 600_000,
  onCourtCount: 5,
  runningClock: false,
  disqualifyingFouls: 0,
  teamFoulsForBonus: 0,
  teamFoulsForDoubleBonus: 0,
  threePointLine: true,
  presentPlayerIds: [],
  startingPlayerIds: [],
};

/**
 * Refuse a Finish or Reopen without sending it when the platform's network
 * adapter says the device is offline. Only then is "it did not go through"
 * provable: an OfflineError from a request that was sent may follow a commit
 * whose answer was lost, and the screen treats that as may-have.
 */
function refuseIfOffline(): void {
  if (!network.isOnline()) {
    throw new OfflineError('offline before the request was sent', false, true);
  }
}

/**
 * Every row in either list, once each by id, in sequence order. `client_seq`
 * is allocated inside the append's transaction (`db.ts`), so it is the order
 * the rows reached disk.
 */
function unionBySeq(
  shown: readonly LoggedEvent[],
  snapshot: readonly LoggedEvent[],
): readonly LoggedEvent[] {
  const byId = new Map<string, LoggedEvent>();
  for (const row of [...shown, ...snapshot]) byId.set(row.id, row);
  return [...byId.values()].sort((left, right) => left.event.clientSeq - right.event.clientSeq);
}

/** The official score in a Finish answer, or null for an answer that does not carry one. */
function officialScoreOf(answer: unknown): FinalScore | null {
  if (typeof answer !== 'object' || answer === null) return null;
  const game: unknown = (answer as { game?: unknown }).game;
  if (typeof game !== 'object' || game === null) return null;
  const { officialScoreUs: us, officialScoreThem: them } = game as Record<string, unknown>;
  return Number.isInteger(us) && Number.isInteger(them)
    ? { us: us as number, them: them as number }
    : null;
}

export function useScorebook(gameId: string): Scorebook {
  const [game, setGame] = useState<CachedGame | null>(null);
  const [refused, setRefused] = useState(false);
  const [log, setLog] = useState<readonly LoggedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SyncStatus>({ kind: 'synced' });
  const [persistence, setPersistence] = useState<Persistence>('unknown');
  const [failure, setFailure] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const engine = useRef<{ drainNow: () => void } | null>(null);
  const correcting = useRef(false);
  /** Put rows that just committed on screen, and tell the other windows on this game. */
  const show = useCallback(
    (rows: readonly LoggedEvent[]) => {
      // Joined by id, as every re-read is: a recovery re-read can already have
      // shown this row while the call that wrote it was still finishing (its
      // connection-recovery retry), and appending it again would count the play
      // twice (Codex P2 on a109f3e).
      setLog((previous) => unionBySeq(previous, rows));
      announceWrite(gameId);
    },
    [gameId],
  );

  useEffect(() => {
    let cancelled = false;
    /** A read of this game's book joined with what is shown of it. */
    const thisGame = (shown: readonly LoggedEvent[], rows: readonly LoggedEvent[]) =>
      unionBySeq(
        shown.filter((row) => row.gameId === gameId),
        rows,
      );

    void (async () => {
      try {
        // The CACHE first, and the network never blocks this screen. A coach
        // arriving at a gym with no signal has to reach a working book, and
        // the acceptance criterion is a cold offline start under two seconds.
        const [cached, rows, signedIn] = await Promise.all([
          cachedGame(gameId),
          logFor(gameId),
          signedInForBooks(),
        ]);
        if (cancelled) return;
        setGame(cached ?? null);
        setRefused(cached !== undefined && !mayOpen(cached.userId, signedIn));
        // Joined, not replaced: no tap can have added a row before the open,
        // but another window's write can (`heard` below), and its re-read may
        // resolve first. A previous game's rows still on screen are dropped.
        setLog((previous) => thisGame(previous, rows));
        setLoading(false);
        void confirmOwner(cached);
      } catch (error) {
        if (cancelled) return;
        setFailure(error);
        setLoading(false);
        return;
      }

      setPersistence(await requestPersistence());
    })();

    /*
     * Then ask the server who is signed in, never blocking the open above.
     *
     * What unlocks a book for its owner after they sign out and back in (the
     * phone's own record says null until somebody names them), what closes it
     * when somebody else's session is on the phone, and what stamps an owner
     * on a book cached before books carried one (ADR-0090). Anything else
     * leaves the decision the open above made from the phone's own record.
     */
    async function confirmOwner(cached: CachedGame | undefined): Promise<void> {
      const userId = await confirmSignedIn();
      if (userId === undefined || cancelled || cached === undefined) return;
      // A session that ran out: closed, exactly as after a sign-out.
      if (userId === null) {
        setRefused(true);
        return;
      }
      // In the store, not from `cached`: the open's copy may predate a `finish()`.
      if (cached.userId === undefined) await stampOwner(gameId, userId).catch(() => undefined);
      if (!cancelled) setRefused(!mayOpen(cached.userId ?? userId, userId));
    }

    const sync = startSync(gameId, (next) => {
      if (!cancelled) setStatus(next);
    });
    engine.current = sync;

    /*
     * Drain the moment the app comes back to the foreground.
     *
     * A backgrounded tab has its timers throttled to a crawl and a locked phone
     * may freeze them outright, so the five second tick cannot be relied on to
     * be the thing that notices. A coach who locks the phone during a timeout
     * and unlocks it at the whistle should not then wait on a timer that has
     * been asleep.
     */
    const resumed = appState.onChange((state) => {
      if (state !== 'active') return;
      sync.drainNow();
      // The web drops the lock on every background and never restores it, so
      // the screen slept after the first one (issue #45). Idempotent.
      void wakeLock.request();
    });

    /*
     * Another window on this game wrote (issue #45). Each window held its own
     * plays in memory while Undo read the shared disk, so the screens
     * disagreed and Undo could void the other window's play. Joined rather
     * than replaced, so a read that resolves after this window's own write
     * cannot take that play off the screen. A refused read is said the way a
     * sync tick says it (`unreadable`), never swallowed.
     */
    const heard = broadcast.subscribe(WRITES, (message) => {
      if (message !== gameId) return;
      logFor(gameId)
        .then((rows) => {
          if (!cancelled) setLog((previous) => thisGame(previous, rows));
        })
        .catch((error: unknown) => {
          if (!cancelled) setStatus(unreadable(error));
        });
    });

    // Requested, never assumed. A refusal is normal and the screen carries on.
    void wakeLock.request();

    return () => {
      cancelled = true;
      sync.stop();
      resumed();
      heard();
      void wakeLock.release();
    };
  }, [gameId, attempt]);

  const setup = game?.setup ?? NO_SETUP;
  const clock = useMemo(() => clockAt(log, now, setup), [log, now, setup]);

  // Only while it is actually running. A stopped clock re-rendering four times
  // a second is battery a coach needs for the second half.
  useEffect(() => {
    if (!clock.running) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [clock.running]);

  /**
   * Every write runs in turn, one at a time and in the order of the taps.
   *
   * Each write reads the book from disk before it appends, and two reads in
   * flight at once may resolve in either order, so without this a second
   * quick tap could take a lower `clientSeq` than the first, or a reading
   * that does not yet include it. A failed write does not hold up the next.
   */
  const turn = useRef<Promise<unknown>>(Promise.resolve());
  const inTurn = useCallback(<T>(work: () => Promise<T>): Promise<T> => {
    const mine = turn.current.then(work);
    turn.current = mine.catch(() => undefined);
    return mine;
  }, []);

  /**
   * Record what one tap means, as a run of rows in ONE transaction.
   *
   * A period change is a `PERIOD_END` and the next `PERIOD_START`; an undo of
   * a swap is two VOID rows. Two transactions mean an app kill between them
   * lands half: a period ended and never reopened, or a floor one player
   * short. One reading for the run, and one tap id (`meta.tap`) for the rows
   * that belong together, which is what marks them as one tap for
   * `voidGroup`. The wall clock is the true time of the run, taken at the
   * same instant as the reading, since `clockAt` counts from it.
   */
  const writeAt = useCallback(
    async (
      count: number,
      make: (reading: ClockState, wallClock: string, tap: string) => readonly EventBody[],
      nowMs: number,
      shown?: number,
    ): Promise<{ readonly rows: readonly LoggedEvent[] | null; readonly current: number }> => {
      const wallClock = new Date(nowMs).toISOString();
      const tap = ids.uuid();
      let current = 0;
      // The reading comes from the book on DISK, never the screen's view of
      // it, and it is read, decided on and appended in ONE transaction
      // (`appendFrom`), so another window's write cannot land in between. The
      // screen can be stale, and a period or clock stamped here is permanent.
      const { log: book, rows } = await appendFrom(gameId, count, (log) => {
        const reading = clockAt(log, nowMs, setup);
        current = reading.period;
        // A tap names the period it was made in; a book that has left it gets
        // nothing (`PeriodMovedError`).
        if (shown !== undefined && reading.period !== shown) return null;
        return make(reading, wallClock, tap);
      });
      if (rows === null) {
        // Nothing written, so no nudge: only this screen was behind.
        setLog((previous) => unionBySeq(previous, book));
        return { rows, current };
      }

      // What the reading was taken from goes on screen with the new rows.
      show([...book, ...rows]);
      engine.current?.drainNow();
      return { rows, current };
    },
    [gameId, setup, show],
  );

  /** Drafts as one tap's rows, at the reading the write takes from disk. */
  const asBodies = useCallback(
    (drafts: readonly EventDraft[]) =>
      (reading: ClockState, wallClock: string, tap: string): readonly EventBody[] =>
        drafts.map((draft) => playBody(nextPeriod(draft, reading, setup), reading, wallClock, tap)),
    [setup],
  );

  const record = useCallback(
    (...drafts: readonly EventDraft[]): Promise<readonly LoggedEvent[]> => {
      if (drafts.length === 0) return Promise.resolve([]);
      // Fired before the write so the thumb gets its answer immediately. It is
      // a no-op on the web today and called anyway, per MOBILE_SUPPLEMENT 2.1.
      haptics.impact('light');
      // The time of the tap, not of the turn it waits for, and the period the
      // screen showed when it was made.
      const nowMs = Date.now();
      const shown = clockAt(log, nowMs, setup).period;
      return inTurn(async () => {
        const { rows, current } = await writeAt(drafts.length, asBodies(drafts), nowMs, shown);
        if (rows === null) throw new PeriodMovedError(shown, current);
        return rows;
      });
    },
    [asBodies, inTurn, log, setup, writeAt],
  );

  /**
   * End the period the screen shows, and open the next, in ONE transaction.
   *
   * The screen names the period it means (`shown`). If the book on disk is no
   * longer in it (another window ended it, or a second tap on End period
   * before the screen caught up), nothing is written and the screen catches
   * up from disk: ending whatever period the disk is in instead would close a
   * period nobody had seen begin, at an unmoved clock. The check and the
   * append are one transaction, so two windows ending the same period at the
   * same moment end it once.
   */
  const endPeriod = useCallback(
    (shown: number): Promise<EndPeriodResult> => {
      haptics.impact('light');
      const nowMs = Date.now();
      return inTurn(async () => {
        const { rows, current } = await writeAt(
          2,
          asBodies([
            { type: 'PERIOD_END', team: 'us', period: shown },
            { type: 'PERIOD_START', team: 'us' },
          ]),
          nowMs,
          shown,
        );
        return rows === null ? { kind: 'already', current } : { kind: 'ended' };
      });
    },
    [asBodies, inTurn, writeAt],
  );

  /**
   * One correction at a time.
   *
   * Two taps inside one render frame both run with the same captured `log`, so
   * both would name the same event and the second would write a redundant VOID
   * instead of reversing the play before it. The server accepts both
   * (`refuseForeignVoid` only checks the target is in this game), so nothing
   * fails loudly: the scorekeeper sees one tap do nothing, and the permanent
   * log keeps a meaningless row forever. Re-rendering cannot close this,
   * because the render happens after the write the two taps are racing on.
   */
  const once = useCallback(async (work: () => Promise<void>): Promise<void> => {
    if (correcting.current) return;
    correcting.current = true;
    try {
      await work();
    } finally {
      correcting.current = false;
    }
  }, []);

  /**
   * Undo: the newest tap reversed, as VOID rows and, for a correction, the
   * original recorded again (`undoDrafts`, ADR-0044).
   *
   * Never a delete and never an edit. The rows it names stay on disk and stay
   * in the outbox, so the server sees the play and the correction in the order
   * they happened. A swap is taken back whole, since a `SUB_IN` voided alone
   * leaves the floor one short for the rest of the game.
   *
   * The target is read from DISK, not from the `log` in this closure, so a tap
   * arriving before React has re-rendered still reverses the newest play rather
   * than the one before it.
   */
  const undo = useCallback(() => {
    const nowMs = Date.now();
    return once(() =>
      inTurn(async () => {
        const drafts = undoDrafts(await logFor(gameId));
        if (drafts.length === 0) return;
        haptics.notification('warning');
        await writeAt(drafts.length, asBodies(drafts), nowMs);
      }),
    );
  }, [asBodies, gameId, inTurn, once, writeAt]);

  /** Void one named play from the timeline, and whatever its tap wrote with it. */
  const voidPlay = useCallback(
    (target: LoggedEvent) => {
      const nowMs = Date.now();
      return once(() =>
        inTurn(async () => {
          const group = voidGroup(liveEvents(await logFor(gameId)), target);
          if (group.length === 0) return;
          haptics.notification('warning');
          await writeAt(group.length, asBodies(group.map(voidDraft)), nowMs);
        }),
      );
    },
    [asBodies, gameId, inTurn, once, writeAt],
  );

  /**
   * Give an unattributed play to a player: two rows (`attributionBodies`),
   * written in ONE transaction so an app kill cannot land half of it.
   */
  const attribute = useCallback(
    (target: LoggedEvent, playerId: string) => {
      // The time of the tap, as every write is stamped, not of its turn.
      const wallClock = new Date(Date.now()).toISOString();
      return once(() =>
        inTurn(async () => {
          // The target is read from DISK, as `voidPlay` reads its own: the
          // timeline's row can outlive the play it names by one render, and a
          // second chip tap in that window wrote a second named copy and a
          // second VOID of the original, counting one basket twice. Found by
          // the fresh-context pass.
          const live = liveEvents(await logFor(gameId));
          if (!live.some((row) => row.id === target.id)) return;
          haptics.impact('light');
          const rows = await queueEvents(
            gameId,
            attributionBodies(target, playerId, wallClock, ids.uuid()),
          );

          show(rows);
          engine.current?.drainNow();
        }),
      );
    },
    [gameId, inTurn, once, show],
  );

  /**
   * Change a foul's kind: the foul again under the new type and a VOID of the
   * original, in ONE transaction, read from disk like `attribute` so a double
   * tap cannot write the pair twice.
   */
  const retype = useCallback(
    async (target: LoggedEvent, type: string): Promise<RetypeResult> => {
      // Read before `once`, which drops a second correction without a word.
      if (correcting.current) return { kind: 'busy' };
      const wallClock = new Date(Date.now()).toISOString();
      const made: { row: LoggedEvent | null; current: string | null } = {
        row: null,
        current: null,
      };
      await once(() =>
        inTurn(async () => {
          // The liveness check is what stops a tap on a button drawn for a foul
          // that has already been changed (two taps before React re-renders)
          // from writing a second copy and a second VOID: the target is read
          // from disk, not from the screen.
          const live = liveEvents(await logFor(gameId));
          if (!live.some((row) => row.id === target.id)) {
            // What the foul stands as now, so the caller can say so.
            made.current =
              live.find((row) => row.event.meta?.['replaces'] === target.id)?.event.type ?? null;
            return;
          }
          haptics.impact('light');
          const rows = await queueEvents(gameId, retypeBodies(target, type, wallClock, ids.uuid()));

          show(rows);
          engine.current?.drainNow();
          made.row = rows[0] ?? null;
        }),
      );
      return made.row === null
        ? { kind: 'gone', current: made.current }
        : { kind: 'done', row: made.row };
    },
    [gameId, inTurn, once, show],
  );

  /**
   * A swap, as two rows at the SAME clock reading (`substitutionBodies`), in
   * ONE transaction: landing only the `SUB_OUT` leaves a floor one player short
   * forever.
   */
  const substitute = useCallback(
    (outPlayerId: string, inPlayerId: string): Promise<void> => {
      haptics.impact('medium');
      const nowMs = Date.now();
      const shown = clockAt(log, nowMs, setup).period;
      return inTurn(async () => {
        // From disk, in one transaction, as `record` writes, so a stale screen
        // can neither stamp the swap nor put it in a period it was not made in.
        const { rows, current } = await writeAt(
          2,
          (reading, wallClock, tap) =>
            substitutionBodies(outPlayerId, inPlayerId, reading, wallClock, tap),
          nowMs,
          shown,
        );
        if (rows === null) throw new PeriodMovedError(shown, current);
      });
    },
    [inTurn, log, setup, writeAt],
  );

  /**
   * Bumped when a status write starts and again when it ends, so a re-read
   * whose cached-game snapshot began before the write finished can tell, and
   * does not put the old status back on screen (`reread`).
   */
  const statusVersion = useRef(0);

  /**
   * What this device believes the book's state is, on disk and on screen.
   *
   * Cached as well as held in state, because the banner that says the book is
   * closed has to survive a restart. Without the cache write a coach who
   * finishes and then locks the phone reopens to a book that looks live and
   * refuses every play with a 409.
   */
  const setCachedStatus = useCallback(
    async (status: CachedGame['status'], officialScore?: FinalScore) => {
      // The kept score belongs to a final book only: a Reopen (or a Finish
      // whose answer carried none) leaves no stale number behind.
      const change = { status, officialScore };
      statusVersion.current += 1;
      try {
        await stampStatus(gameId, status, officialScore);
        setGame((previous) => (previous === null ? null : { ...previous, ...change }));
      } finally {
        statusVersion.current += 1;
      }
    },
    [gameId],
  );

  /**
   * Take a finished game off the shelf so the outbox can land (ADR-0047).
   *
   * Offered to whoever hit the 409, without asking what role they hold: the
   * reopen route is behind the same `requireWrite` guard as the sync that was
   * just refused, so a device whose plays were rejected for this reason is by
   * definition allowed to do this. The plays were never lost, they were waiting.
   */
  const reopen = useCallback(async () => {
    refuseIfOffline();
    await post(`/api/scorebook/games/${gameId}/reopen`, {});
    await afterServer(() => setCachedStatus('live'));
    engine.current?.drainNow();
  }, [gameId, setCachedStatus]);

  const publishedOnServer = useCallback(async (): Promise<boolean> => {
    try {
      const answer = await api<{ game?: { book?: unknown } | null }>(
        `/api/scorebook/games/${gameId}`,
        {},
        { timeoutMs: 5_000 },
      );
      return answer.game?.book === 'published';
    } catch {
      return false;
    }
  }, [gameId]);

  /**
   * Close the book.
   *
   * The caller is responsible for the outbox being empty first, and the screen
   * enforces it: finishing with events still queued means the server refuses
   * them afterwards with `GAME_FINALIZED` and they sit on the phone until
   * somebody reopens the game.
   *
   * The score is the coach's, not a derivation. It lands in the OFFICIAL
   * columns beside the derived pair (BUILD_PLAN 13.8 D10), which is why the
   * screen offers this book's arithmetic as a starting point rather than
   * sending it unasked.
   *
   * A Finish of a game that is already final is answered 200 with the stored
   * row (`finalizeGame` reports `finalized: false`), never a 409:
   * `GAME_FINALIZED` is raised only by the event append. So a second Finish is
   * an ordinary success here, carrying the FIRST committed score.
   */
  const finish = useCallback(
    async (ourScore: number, theirScore: number): Promise<FinalScore | null> => {
      refuseIfOffline();
      // The route answers `{ game }` with the stored row, whose official score
      // is the FIRST one committed: a second Finish of a final game changes
      // nothing (`finalizeGame`). That, not what was typed, is what the phone
      // shows and keeps.
      const stored = officialScoreOf(
        await post<unknown>(`/api/scorebook/games/${gameId}/finish`, { ourScore, theirScore }),
      );
      await afterServer(() => setCachedStatus('final', stored ?? undefined));
      return stored;
    },
    [gameId, setCachedStatus],
  );

  return {
    game,
    refused,
    log,
    plays: useMemo(() => liveEvents(log), [log]),
    status,
    persistence,
    loading,
    failure,
    retryOpen: useCallback(() => {
      setFailure(null);
      setLoading(true);
      setAttempt((count) => count + 1);
    }, []),
    finished: game?.status === 'final',
    clock,
    scores: useMemo(() => tally(log), [log]),
    floor: useMemo(() => onCourt(log, setup), [log, setup]),
    canUndo: useMemo(() => undoDrafts(log).length > 0, [log]),
    record,
    undo,
    voidPlay,
    attribute,
    retype,
    substitute,
    endPeriod,
    retryNow: () => engine.current?.drainNow(),
    reopen,
    publishedOnServer,
    finish,
    unsyncedHere: useCallback(() => unsyncedCount(gameId), [gameId]),
    reread: useCallback(async () => {
      // Events are append-only, so outside a wipe or a takeover (where this
      // read throws) the log on screen may only grow. The snapshot is joined
      // with what is shown, by id and in sequence order, so no snapshot of any
      // age (one taken before a tap committed, or one overtaken by a newer
      // recovery read) can take a shown row away (Codex P2 on ea11236).
      const version = statusVersion.current;
      const [cached, rows] = await Promise.all([cachedGame(gameId), logFor(gameId)]);
      // The same for the status: a snapshot older than the last status write
      // must not overwrite it. If the write began before this read, or ended
      // during it, the version moved and the snapshot is dropped.
      if (cached !== undefined && statusVersion.current === version) setGame(cached);
      setLog((previous) => unionBySeq(previous, rows));
      // A write whose outcome was unknown reaches the screen here rather than
      // through `show`, so this is that write's nudge to the other windows.
      announceWrite(gameId);
    }, [gameId]),
  };
}
